import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../api/client';
import { Database, Plus, Save, Trash2, Edit2 } from 'lucide-react';
import { useCan, Can } from '../../../lib/rbac';

interface MetadataTabProps {
  product: any;
  sellerId: number;
}

export default function MetadataTab({ product, sellerId }: MetadataTabProps) {
  const can = useCan();
  const canEdit = can('catalog.edit_listing');
  const canMoney = can('global.view_money');
  const [metadata, setMetadata] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    url_reference: '',
    price_reference: '',
    price_reference_currency: 'USD',
    additional_info: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<any>(`/catalog/products/${product.item_id}/metadata?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setMetadata(res.data);
      } else {
        setMetadata(null);
      }
    } catch {
      setMetadata(null);
    }
    setLoading(false);
  }, [product.item_id, sellerId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Pre-fill form for create mode
  const getDefaultForm = useCallback(() => ({
    url_reference: product.asin ? `https://www.amazon.com/dp/${product.asin}` : '',
    price_reference: product.amazon_price ? String(product.amazon_price) : '',
    price_reference_currency: 'USD',
    additional_info: '',
  }), [product.asin, product.amazon_price]);

  const handleCreate = async () => {
    setError(null);
    // Validate
    if (!form.url_reference.trim()) {
      setError('URL de referencia es requerida');
      return;
    }
    if (!form.price_reference || isNaN(Number(form.price_reference)) || Number(form.price_reference) <= 0) {
      setError('Precio de referencia debe ser un numero positivo');
      return;
    }
    if (form.additional_info.trim()) {
      try {
        JSON.parse(form.additional_info);
      } catch {
        setError('additional_info debe ser JSON valido');
        return;
      }
    }

    setSaving(true);
    try {
      const body: any = {
        url_reference: form.url_reference.trim(),
        price_reference: Number(form.price_reference),
        price_reference_currency: form.price_reference_currency,
      };
      if (form.additional_info.trim()) {
        body.additional_info = JSON.parse(form.additional_info);
      }
      const res = await api.post(`/catalog/products/${product.item_id}/metadata?seller_id=${sellerId}`, body);
      if (res.success) {
        await loadMetadata();
        setEditing(false);
      } else {
        setError((res as any).error || 'Error al crear metadata');
      }
    } catch (e: any) {
      setError(e.message || 'Error al crear metadata');
    }
    setSaving(false);
  };

  const handleUpdate = async () => {
    setError(null);
    if (!form.url_reference.trim()) {
      setError('URL de referencia es requerida');
      return;
    }
    if (!form.price_reference || isNaN(Number(form.price_reference)) || Number(form.price_reference) <= 0) {
      setError('Precio de referencia debe ser un numero positivo');
      return;
    }
    if (form.additional_info.trim()) {
      try {
        JSON.parse(form.additional_info);
      } catch {
        setError('additional_info debe ser JSON valido');
        return;
      }
    }

    setSaving(true);
    try {
      const body: any = {
        url_reference: form.url_reference.trim(),
        price_reference: Number(form.price_reference),
        price_reference_currency: form.price_reference_currency,
      };
      if (form.additional_info.trim()) {
        body.additional_info = JSON.parse(form.additional_info);
      }
      const res = await api.put(`/catalog/products/${product.item_id}/metadata?seller_id=${sellerId}`, body);
      if (res.success) {
        await loadMetadata();
        setEditing(false);
      } else {
        setError((res as any).error || 'Error al actualizar metadata');
      }
    } catch (e: any) {
      setError(e.message || 'Error al actualizar metadata');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.delete(`/catalog/products/${product.item_id}/metadata?seller_id=${sellerId}`);
      if (res.success) {
        setMetadata(null);
        setEditing(false);
        setConfirmDelete(false);
        setForm(getDefaultForm());
      } else {
        setError((res as any).error || 'Error al eliminar metadata');
      }
    } catch (e: any) {
      setError(e.message || 'Error al eliminar metadata');
    }
    setSaving(false);
  };

  const startEditing = () => {
    if (metadata) {
      setForm({
        url_reference: metadata.url_reference || '',
        price_reference: metadata.price_reference != null ? String(metadata.price_reference) : '',
        price_reference_currency: metadata.price_reference_currency || 'USD',
        additional_info: metadata.additional_info ? JSON.stringify(metadata.additional_info, null, 2) : '',
      });
    }
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(null);
    setConfirmDelete(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isCreateMode = !metadata;

  // Initialize form for create mode on first render without metadata
  if (isCreateMode && !editing && form.url_reference === '' && form.price_reference === '') {
    setForm(getDefaultForm());
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-[var(--color-text-muted)]" />
          <h3 className="text-sm font-semibold text-[var(--color-text)]">ML Metadata</h3>
          {metadata ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Metadata configurada
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              Sin metadata
            </span>
          )}
        </div>
        {metadata && !editing && (
          <div className="flex items-center gap-2">
            <Can cap="catalog.edit_listing">
            <button
              onClick={startEditing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text)] transition-colors"
            >
              <Edit2 size={14} />
              Editar
            </button>
            </Can>
            <Can cap="catalog.close_delete">
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 hover:bg-red-50 text-red-600 dark:border-red-800 dark:hover:bg-red-900/20 dark:text-red-400 transition-colors"
            >
              <Trash2 size={14} />
              Eliminar
            </button>
            </Can>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-400 mb-3">
            Estas seguro de eliminar la metadata de este producto? Esta accion no se puede deshacer.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
            >
              {saving ? 'Eliminando...' : 'Si, eliminar'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text)] transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* View mode - metadata exists */}
      {metadata && !editing && !confirmDelete && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">URL de Referencia</div>
            <a
              href={metadata.url_reference}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[var(--color-primary)] hover:underline break-all"
            >
              {metadata.url_reference}
            </a>
          </div>

          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Precio de Referencia</div>
            <div className="text-sm font-medium text-[var(--color-text)]">
              {canMoney ? `$${metadata.price_reference} ${metadata.price_reference_currency}` : '\u2014'}
            </div>
          </div>

          {metadata.additional_info && (
            <div className="md:col-span-2 p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
              <div className="text-xs text-[var(--color-text-muted)] mb-1">Informacion Adicional</div>
              <pre className="text-xs text-[var(--color-text)] font-mono bg-[var(--color-surface)] p-2 rounded overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(metadata.additional_info, null, 2)}
              </pre>
            </div>
          )}

          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Creado</div>
            <div className="text-sm text-[var(--color-text)]">
              {metadata.date_created ? new Date(metadata.date_created).toLocaleString() : '—'}
            </div>
          </div>

          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">Actualizado</div>
            <div className="text-sm text-[var(--color-text)]">
              {metadata.date_updated ? new Date(metadata.date_updated).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Sin metadata y sin permiso de editar: aviso de una línea en vez de pestaña en blanco. */}
      {isCreateMode && !confirmDelete && !canEdit && (
        <p className="text-sm text-[var(--color-text-muted)]">
          Este producto todavía no tiene metadata cargada y tu rol no permite cargarla.
        </p>
      )}

      {/* Create/Edit form */}
      {(isCreateMode || editing) && !confirmDelete && canEdit && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                URL de Referencia *
              </label>
              <input
                type="url"
                value={form.url_reference}
                onChange={(e) => setForm({ ...form, url_reference: e.target.value })}
                placeholder="https://www.amazon.com/dp/..."
                className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  Precio Referencia *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price_reference}
                  onChange={(e) => setForm({ ...form, price_reference: e.target.value })}
                  placeholder="29.99"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  Moneda
                </label>
                <select
                  value={form.price_reference_currency}
                  onChange={(e) => setForm({ ...form, price_reference_currency: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)]"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="BRL">BRL</option>
                  <option value="MXN">MXN</option>
                  <option value="ARS">ARS</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              Informacion Adicional (JSON, opcional)
            </label>
            <textarea
              value={form.additional_info}
              onChange={(e) => setForm({ ...form, additional_info: e.target.value })}
              rows={4}
              placeholder='{"key": "value"}'
              className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] resize-y"
            />
          </div>

          <div className="flex items-center gap-2">
            {isCreateMode ? (
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Plus size={16} />
                )}
                {saving ? 'Creando...' : 'Crear Metadata'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={cancelEditing}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text)] transition-colors"
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
